import { createSlice } from '@reduxjs/toolkit'
import { Agreement } from '../../types/agreement'
import { createAsyncAction } from '../actions'

export type AgreementState = {
  agreements: Agreement[]
  agreement?: Agreement
}

export const initialAgreementState: AgreementState = {
  agreements: [],
}

export const AgreementActions = {
  fetchAgreements: createAsyncAction<void, { agreements: Agreement[] }>(
    'agreement:fetchAgreements',
    async (params, { agreementRepository }) => {
      const agreements = await agreementRepository.loadAgreements()
      return { agreements }
    }
  ),
  fetchAgreement: createAsyncAction<{ agreementId: string }, { agreement: Agreement }>(
    'agreement:fetchAgreement',
    async (params, { agreementRepository }) => {
      const agreement = await agreementRepository.loadAgreement(params.agreementId)
      return { agreement }
    }
  ),
  createAgreement: createAsyncAction<
    {
      name: string
      clientId: string
      file: File
    },
    { agreements: Agreement[] }
  >('agreement:createAgreement', async (params, { agreementRepository }) => {
    await agreementRepository.postAgreement(params.name, params.clientId, params.file)
    const agreements = await agreementRepository.loadAgreements()
    return { agreements }
  }),
  acceptAgreement: createAsyncAction<{ agreementId: string }, { agreement: Agreement }>(
    'agreement:acceptAgreement',
    async (params, { agreementRepository }) => {
      await agreementRepository.acceptAgreement(params.agreementId)
      const agreement = await agreementRepository.loadAgreement(params.agreementId)
      return { agreement }
    }
  ),
  rejectAgreement: createAsyncAction<{ agreementId: string; reason: string }, { agreement: Agreement }>(
    'agreement:rejectAgreement',
    async (params, { agreementRepository }) => {
      await agreementRepository.rejectAgreement(params.agreementId, params.reason)
      const agreement = await agreementRepository.loadAgreement(params.agreementId)
      return { agreement }
    }
  ),
}

const agreementSlice = createSlice({
  name: 'agreement',
  initialState: initialAgreementState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(AgreementActions.fetchAgreements.fulfilled, (state, action) => {
        state.agreements = action.payload.agreements
      })
      .addCase(AgreementActions.fetchAgreement.pending, (state) => {
        state.agreement = undefined
      })
      .addCase(AgreementActions.fetchAgreement.fulfilled, (state, action) => {
        state.agreement = action.payload.agreement
      })
      .addCase(AgreementActions.createAgreement.fulfilled, (state, action) => {
        state.agreements = action.payload.agreements
      })
      .addCase(AgreementActions.acceptAgreement.fulfilled, (state, action) => {
        state.agreement = action.payload.agreement
      })
      .addCase(AgreementActions.rejectAgreement.fulfilled, (state, action) => {
        state.agreement = action.payload.agreement
      })
  },
})

export default agreementSlice
